import React from "react";
import { Box, IconButton, MenuItem, Slider, TextField, Typography } from "@mui/material";
import { CheckCircle, HelpOutline, Info, JoinInner, Warning } from "@mui/icons-material";

// Shared by KnowledgeBaseDialog (DIM1) and KnowledgeBaseD3Dialog (DIM3).
// Values are case-sensitive on the backend; an unknown value is treated as Basic.
export const RETRIEVAL_TYPES = [
  { value: "Basic", help: "Finds passages that mean the same as the question.", Icon: CheckCircle, color: "#4caf50" },
  { value: "Hybrid", help: "Also matches exact words, such as names and codes.", Icon: JoinInner, color: "#8b5cf6" },
  { value: "MMR", help: "Avoids several results that say the same thing.", Icon: Info, color: "#2196f3" },
  { value: "HyDE", help: "Helps when questions are short or vague.", Icon: Warning, color: "#ff9800" },
];

export const DEFAULT_RECENCY_WEIGHT = 0.3;

// MUI Select needs MenuItem as direct children, so this returns the items
// rather than wrapping them in a component.
export const renderRetrievalTypeOptions = () =>
  RETRIEVAL_TYPES.map(({ value, help, Icon, color }) => (
    <MenuItem key={value} value={value}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
        <Icon sx={{ fontSize: 16, color }} />
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {value}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {help}
          </Typography>
        </Box>
      </Box>
    </MenuItem>
  ));

// Applies a recency patch to one knowledge-base entry. `undefined` removes the
// key: the backend reads an absent key as "off" / "use the default".
export const applyRecencyPatch = (kb, patch) => {
  const next = { ...kb, ...patch };
  Object.keys(patch).forEach((key) => {
    if (patch[key] === undefined) delete next[key];
  });
  return next;
};

/**
 * "Prefer recent documents" controls for one knowledge-base entry.
 * recencyHalfLifeDays: empty / 0 = off; recencyWeight (0–1) only applies when
 * a half-life is set. `onChange` receives a patch for applyRecencyPatch.
 * `InfoTooltip` is the host dialog's tooltip component.
 */
export const KnowledgeBaseRecencyFields = ({ kb, onChange, styles, InfoTooltip }) => {
  const halfLife = Number(kb?.recencyHalfLifeDays) > 0 ? Number(kb.recencyHalfLifeDays) : null;
  const savedWeight = Number(kb?.recencyWeight);
  const weight =
    kb?.recencyWeight !== undefined && kb?.recencyWeight !== null && savedWeight >= 0 && savedWeight <= 1
      ? savedWeight
      : DEFAULT_RECENCY_WEIGHT;

  return (
    <>
      <Box className={styles.section}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography variant="body2" className={styles.sectionLabel}>
            Prefer recent documents (half-life in days)
          </Typography>
          <InfoTooltip
            title="Prefer Recent Documents Guide"
            maxWidth={800}
            placement="right"
            content={
              <Box>
                <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.9)" }}>
                  <strong>What it does:</strong> Gives newer documents a small boost, so they show up ahead of older ones
                </Typography>

                <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                  📊 How it works:
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                  • Empty = Off. Results are ranked by relevance only
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                  • The number is the age, in days, at which a document loses half of its boost
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                  • Smaller number = newer documents are favoured more strongly
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                  • Age is counted from when the document was last uploaded or re-processed, not a date written inside it
                </Typography>

                <Typography variant="body2" sx={{ mb: 1, color: "#2196f3", fontWeight: 600 }}>
                  💡 Examples:
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                  • <strong>7:</strong> "What is the current offer?" → This week's price list comes before last month's
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                  • <strong>30:</strong> "What is our leave policy?" → The policy uploaded this month is preferred over last year's copy
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                  • <strong>365:</strong> Only documents more than a year old are noticeably pushed down
                </Typography>

                <Typography variant="body2" sx={{ mb: 1, color: "#ff9800", fontWeight: 600 }}>
                  ⚠️ Recommendations:
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                  • Leave empty when older documents are just as valid as new ones
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                  • Start with 30 for content that changes often, such as prices or policies
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                  • It is a nudge, not a sort by date: a clearly more relevant old document still wins
                </Typography>
              </Box>
            }
          >
            <IconButton size="small" sx={{ color: "#e36250" }}>
              <HelpOutline sx={{ fontSize: 18 }} />
            </IconButton>
          </InfoTooltip>
        </Box>
        <TextField
          type="number"
          value={halfLife ?? ""}
          placeholder="30"
          onChange={(e) => {
            const days = Number(e.target.value);
            onChange(
              e.target.value !== "" && days > 0
                ? { recencyHalfLifeDays: days }
                : { recencyHalfLifeDays: undefined, recencyWeight: undefined }
            );
          }}
          size="small"
          fullWidth
          className={styles.textField}
          inputProps={{ min: 0 }}
          helperText="Leave empty to rank by relevance only."
        />
      </Box>

      {halfLife !== null && (
        <Box className={styles.section}>
          <Typography variant="body2" className={styles.sectionLabel}>
            Recency strength: {weight}
          </Typography>
          <Box className={styles.sliderContainer}>
            <Slider
              value={weight}
              onChange={(e, value) => onChange({ recencyWeight: value })}
              min={0}
              max={1}
              step={0.1}
              valueLabelDisplay="auto"
              className={styles.slider}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            0 ignores age; 1 ranks by age alone and is not recommended.
          </Typography>
        </Box>
      )}
    </>
  );
};
